import { useMedia } from "shared/providers";
import {
  Button,
  ButtonSize,
  IconButton,
  IconButtonSize
} from "shared/components";
import {
  ArrowNarrowLeft,
  ArrowNarrowRight,
  ChevronLeft,
  ChevronLeftDouble,
  ChevronRight,
  ChevronRightDouble
} from "untitledui-js-base";
import { cx } from "utils/classnames";

const maxPagesDesktop = 6;
const maxPagesMobile = 3;
const maxPagesAheadDesktop = 3;
const maxPagesAheadMobile = 1;

export const Pagination = ({
  total,
  current,
  className,
  onChange
}: {
  total: number;
  current: number;
  className?: string;
  onChange: (newPage: number) => void;
}) => {
  const { isMobile } = useMedia();
  if (!total || total === 1) return null;

  const canGoNext = current < total;
  const canGoBack = current > 1;
  const canGoFirst = current > 1;
  const canGoLast = current < total;

  return (
    <div className={cx("flex w-full items-center gap-2", className)}>
      <IconButton
        size={IconButtonSize.SM}
        onClick={() => {
          if (canGoFirst) onChange(1);
        }}
        disabled={!canGoFirst}
        Icon={ChevronLeftDouble}
        className="text-xs shrink-0"
      />
      <Button
        size={ButtonSize.SM}
        onClick={() => {
          if (canGoBack) onChange(current - 1);
        }}
        disabled={!canGoBack}
        Icon={ArrowNarrowLeft}
        className="text-xs shrink-0 md:flex hidden"
      >
        Prev
      </Button>

      <IconButton
        size={IconButtonSize.SM}
        onClick={() => {
          if (canGoBack) onChange(current - 1);
        }}
        disabled={!canGoBack}
        Icon={ChevronLeft}
        className="text-xs shrink-0 block md:hidden"
      />

      <div className="overflow-x-auto">
        <div className="flex gap-0.5 gap-2">
          {Array(total)
            .fill("")
            .map((_: undefined, idx) => {
              // show only 6 pages at a time
              // with current page in the middle
              const numberOfPagesToShow = isMobile
                ? maxPagesMobile
                : maxPagesDesktop;

              const maxPagesAhead = isMobile
                ? maxPagesAheadMobile
                : maxPagesAheadDesktop;

              if (total > numberOfPagesToShow)
                if (current < 4) {
                  if (idx > numberOfPagesToShow - 1) return null;
                } else if (current > total - maxPagesAhead) {
                  if (idx < total - numberOfPagesToShow) return null;
                } else if (
                  idx < current - maxPagesAhead ||
                  idx > current + maxPagesAhead
                )
                  return null;

              return (
                <Button
                  key={String(idx) + "-" + String(total)}
                  size={ButtonSize.SM}
                  onClick={() => {
                    onChange(idx + 1);
                  }}
                  className={cx(
                    "max-w-[40px] text-xs",
                    current === idx + 1 ? "border-2 border-primary-500" : ""
                  )}
                >
                  {idx + 1}
                </Button>
              );
            })}
        </div>
      </div>
      <Button
        size={ButtonSize.SM}
        onClick={() => {
          if (canGoNext) onChange(current + 1);
        }}
        disabled={!canGoNext}
        Icon={ArrowNarrowRight}
        iconPosition="right"
        className="text-xs shrink-0 hidden md:flex"
      >
        Next
      </Button>

      <IconButton
        size={IconButtonSize.SM}
        onClick={() => {
          if (canGoNext) onChange(current + 1);
        }}
        disabled={!canGoLast}
        Icon={ChevronRight}
        className="text-xs shrink-0 block md:hidden"
      />

      <IconButton
        size={IconButtonSize.SM}
        onClick={() => {
          if (canGoLast) onChange(total);
        }}
        disabled={!canGoLast}
        Icon={ChevronRightDouble}
        className="text-xs shrink-0"
      />
    </div>
  );
};
